import {
  differenceInDays,
  format,
  endOfDay,
  isValid,
  parseISO,
} from "date-fns";
import moment from "moment";

export function getSpecificDateRange(
  daysAgo = 30,
  identifierName = "createDate"
) {
  const today = endOfDay(new Date());
  let monthAgo = new Date().setDate(today.getDate() - daysAgo);
  monthAgo = new Date(monthAgo);
  monthAgo.setHours(0, 0, 0, 0);
  return {
    identifier: identifierName,
    dateMin: monthAgo.toISOString(),
    dateMax: today.toISOString(),
  };
}

export function formatDate(date, options = {}) {
  return date
    ? new Date(date).toLocaleDateString("en-US", {
        timeZone: "UTC",
        ...(options.padded
          ? { month: "2-digit", day: "2-digit", year: "numeric" }
          : {}),
      })
    : "";
}

export function formatDateTime(date) {
  return date
    ? new Date(date).toLocaleString("en-US", { timeZone: "UTC" })
    : "";
}

export function formatDateSecondary(date) {
  const utcDate = new Date(date).toLocaleDateString("en-US", {
    timeZone: "UTC",
  });
  return date ? format(new Date(utcDate), "yyyy-MM-dd") : null;
}

export function isDateError(date) {
  const minDate = new Date("1900-1-1");
  const maxDate = new Date("2099-12-31");
  const parsedDate = new Date(date);

  if (isNaN(parsedDate.getTime())) {
    return "Date is not valid";
  } else if (parsedDate < minDate) {
    return "Date should not be before minimal date";
  } else if (parsedDate > maxDate) {
    return "Date should not be after maximum date";
  } else {
    return false;
  }
}

// Default date filter - Past 90 days
export function getCurrentDateDefaultFilter(days = 90) {
  const today = new Date();
  let monthAgo = new Date().setDate(today.getDate() - days);
  monthAgo = new Date(monthAgo);
  monthAgo.setHours(0, 0, 0, 0);

  return {
    dateMin: new Date(
      moment(monthAgo).clone().startOf("day").format("YYYY-MM-DDTHH:mm:ss")
    ).toISOString(),
    dateMax: new Date(
      moment(today).clone().endOf("day").format("YYYY-MM-DDTHH:mm:ss")
    ).toISOString(),
  };
}

export function setDateToUTCNoon(strDate) {
  return new Date(new Date(strDate).setUTCHours(12, 0, 0, 0)).toISOString();
}

export const formatDateStr = (date) => {
  if (!date) return "";
  return format(new Date(date), "MMM d, y h:mm a");
};

export const formatShortDateStr = (date) => {
  if (!date) return "";
  return format(new Date(date), "MMM d, y");
};

/**
 * Helper to check if a given number of days has passed from a provided date.
 * @param {string} date - The ISO date string to check from (e.g., "2024-12-26T21:16:07.650Z").
 * @param {number} days - The number of days to check.
 * @returns {boolean} - Returns true if the number of days has passed, otherwise false.
 */
export const hasDaysPassed = (date, days) => {
  if (!date || typeof days !== "number") {
    throw new Error(
      "Invalid input: date must be a valid string and days a number."
    );
  }

  const parsedDate = moment(date).utc();
  const now = moment().utc();

  const diff = differenceInDays(now.toDate(), parsedDate.toDate());

  return diff >= days;
};

export const getBrowserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export const toISODate = (raw) => {
  if (!raw) return "";

  if (raw instanceof Date && isValid(raw)) {
    return format(raw, "yyyy-MM-dd");
  }

  const s = String(raw).trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const d = parseISO(s);
    return isValid(d) ? format(d, "yyyy-MM-dd") : "";
  }

  const mmddyyyy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
  const m = s.match(mmddyyyy);
  if (m) {
    const [, mm, dd, yyyy] = m;
    const iso = `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
    const d = new Date(iso);
    return isValid(d) ? iso : "";
  }

  const d = parseISO(s);
  return isValid(d) ? format(d, "yyyy-MM-dd") : "";
};
